import React, { useEffect, useState } from 'react';
import { Loader2, Check, Copy, RefreshCw, Plug } from 'lucide-react';
import Header from '../components/Layout/Header';
import { agentService } from '../services/api';
import { Agent } from '../types';
import toast from 'react-hot-toast';
import axios from 'axios';
import { ADMIN_KEY } from '../config/api';

const API = process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';

interface PluginConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: string;
  status: 'available' | 'connected' | 'coming_soon';
  color: string;
  revenue: string;
  setupSteps: string[];
}

const PLUGINS: PluginConfig[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT Plugin',
    icon: '🤖',
    description: 'Publish your agent as a ChatGPT plugin. Users install it in ChatGPT and query your legal AI directly from there.',
    category: 'AI Platforms',
    status: 'available',
    color: '#10a37f',
    revenue: '₹50/token (same billing — ChatGPT is just the frontend)',
    setupSteps: [
      'Select which agent to expose',
      'Generate OpenAPI spec + ai-plugin.json',
      'Deploy plugin manifest to your domain',
      'Submit to OpenAI plugin store',
    ],
  },
  {
    id: 'claude-mcp',
    name: 'Claude MCP Server',
    icon: '🧠',
    description: 'Make your agent available as a Claude tool via Model Context Protocol. Claude users can invoke your legal AI as an MCP tool.',
    category: 'AI Platforms',
    status: 'available',
    color: '#d97706',
    revenue: '₹50/token (Claude invokes your API — you bill per query)',
    setupSteps: [
      'Select which agent to expose',
      'Generate MCP server configuration',
      'Download MCP server package (Node.js)',
      'Users add it to their Claude config',
    ],
  },
  {
    id: 'telegram',
    name: 'Telegram Bot (Lawyer Assistant)',
    icon: '✈️',
    description: 'Your AI agent as a private Telegram bot for lawyers. Research on the go — ask legal questions from your phone while in court, traveling, or between hearings. Personal assistant, not client-facing.',
    category: 'Messaging',
    status: 'available',
    color: '#0088cc',
    revenue: '₹50/token (each message deducts tokens)',
    setupSteps: [
      'Create a Telegram bot via @BotFather',
      'Select which agent to expose',
      'Generate webhook handler code',
      'Deploy to your server and set webhook URL',
    ],
  },
  {
    id: 'instagram',
    name: 'Instagram (Legal Awareness)',
    icon: '📸',
    description: 'Auto-reply to Instagram DMs with general legal awareness information. Positioned as a "Know Your Rights" social awareness bot — NOT legal advice. Includes mandatory disclaimers in every response.',
    category: 'Social Awareness',
    status: 'available',
    color: '#e1306c',
    revenue: '₹5/message + awareness reach',
    setupSteps: [
      'Connect Instagram Business account via Meta API',
      'Select which agent handles DMs',
      'Configure awareness disclaimer template',
      'Set auto-reply rules (general info only, no case-specific advice)',
    ],
  },
  {
    id: 'acrobat',
    name: 'Adobe Acrobat Reader',
    icon: '📄',
    description: 'AI sidebar for PDF analysis. Summarize judgments, extract clauses, find risks, and ask questions about any PDF document.',
    category: 'Desktop Apps',
    status: 'available',
    color: '#ec1c24',
    revenue: '₹50/token (2-5 tokens per document analysis)',
    setupSteps: [
      'Select which agent to use',
      'Generate Office Add-in manifest',
      'Host taskpane.html on your server',
      'Sideload or publish to Adobe Exchange',
    ],
  },
  {
    id: 'ms-office',
    name: 'Microsoft Office',
    icon: '📝',
    description: 'AI sidebar for Word, Excel & PowerPoint. Draft legal docs, translate, summarize, and insert AI text directly into Office.',
    category: 'Desktop Apps',
    status: 'available',
    color: '#0078d4',
    revenue: '₹50/token (1-3 tokens per action)',
    setupSteps: [
      'Select which agent to use',
      'Generate Office Web Add-in manifest',
      'Upload to Microsoft 365 admin center',
      'Users install from Office Add-ins store',
    ],
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp Business Bot',
    icon: '💬',
    description: 'AI-powered replies on WhatsApp Business. Lawyer uses it as a private research assistant on WhatsApp, or sets up auto-replies for general legal information queries.',
    category: 'Messaging',
    status: 'coming_soon',
    color: '#25d366',
    revenue: '₹50/token + WhatsApp Business API fees',
    setupSteps: [
      'Connect WhatsApp Business API (Meta)',
      'Select agent',
      'Configure response rules',
      'Go live',
    ],
  },
];

const PluginsPage: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlugin, setSelectedPlugin] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, clientsRes] = await Promise.all([
          agentService.getAll(),
          axios.get(`${API}/api/deployment/clients`, { headers: { 'x-superadmin-key': ADMIN_KEY } }).catch(() => ({ data: { clients: [] } })),
        ]);
        setAgents(a);
        setClients(clientsRes.data.clients || []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleGenerate = async (pluginId: string) => {
    if (!selectedAgentId) { toast.error('Select an agent first'); return; }
    if (!selectedClientId) { toast.error('Select a client first — tokens will be billed to this client'); return; }
    setGenerating(true);
    setGeneratedConfig(null);
    try {
      const { data } = await axios.post(`${API}/api/plugins/generate`, {
        pluginId,
        agentId: selectedAgentId,
        clientId: selectedClientId,
      }, {
        headers: { 'x-superadmin-key': ADMIN_KEY },
      });
      if (data.success) {
        setGeneratedConfig(data);
        toast.success(`${pluginId} plugin generated for client!`);
      } else {
        toast.error(data.error || 'Generation failed');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message);
    }
    setGenerating(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) return (
    <div className="flex flex-col h-screen">
      <Header title="Plugins" subtitle="Extend your agent to other platforms" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen">
      <Header title="Plugins" subtitle="Connect your AI agents to ChatGPT, Claude, Instagram & more" />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">

          {/* Revenue banner */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Plug size={24} />
              <h2 className="text-lg font-bold">Multi-Channel Revenue</h2>
            </div>
            <p className="text-sm text-white/80 mb-3">Every plugin is a new revenue channel. Same token billing, different frontends. More channels = more queries = more ₹</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold">5</div>
                <div className="text-[10px] text-white/70">Plugins Available</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold">₹50</div>
                <div className="text-[10px] text-white/70">Per Token (all channels)</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold">∞</div>
                <div className="text-[10px] text-white/70">Query Sources</div>
              </div>
            </div>
          </div>

          {/* Plugin Grid */}
          {!selectedPlugin && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {PLUGINS.map(plugin => (
                <div key={plugin.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => setSelectedPlugin(plugin.id)}>
                  <div className="p-1.5">
                    <div className="rounded-xl p-5" style={{ background: `${plugin.color}10` }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl" style={{ background: `${plugin.color}20` }}>
                          {plugin.icon}
                        </div>
                        <span className={`text-[9px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${
                          plugin.status === 'available' ? 'bg-green-100 text-green-700' : 
                          plugin.status === 'connected' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {plugin.status === 'available' ? '● Ready' : plugin.status === 'connected' ? '✓ Connected' : 'Coming Soon'}
                        </span>
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1">{plugin.name}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{plugin.description}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] px-2 py-0.5 bg-white border border-gray-200 rounded-full text-gray-600">{plugin.category}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pb-4 pt-2">
                    <div className="text-[10px] text-gray-500 mb-2">💰 Revenue: <span className="font-semibold text-gray-700">{plugin.revenue}</span></div>
                    <button className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-xs font-semibold hover:bg-gray-800 transition-colors group-hover:bg-indigo-600">
                      Configure →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Plugin Detail / Setup */}
          {selectedPlugin && (() => {
            const plugin = PLUGINS.find(p => p.id === selectedPlugin)!;
            return (
              <div>
                <button onClick={() => { setSelectedPlugin(null); setGeneratedConfig(null); }} className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1">
                  ← Back to all plugins
                </button>

                <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl" style={{ background: `${plugin.color}15` }}>
                      {plugin.icon}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{plugin.name}</h2>
                      <p className="text-sm text-gray-500">{plugin.description}</p>
                    </div>
                  </div>

                  {/* Setup Steps */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <h3 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Setup Steps</h3>
                    <div className="space-y-2">
                      {plugin.setupSteps.map((step, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</div>
                          <span className="text-sm text-gray-700">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Client Selection — tokens billed to this client */}
                  <div className="mb-4">
                    <label className="text-xs font-bold text-gray-700 block mb-2">Select Client (billing target)</label>
                    <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500">
                      <option value="">Choose a client — tokens deduct from their balance...</option>
                      {clients.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.id}) — {c.tokens?.remaining?.toFixed(1) || 0} tokens remaining
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-1">All plugin queries will consume tokens from this client's balance. The client's API key will be used in the generated config.</p>
                  </div>

                  {/* Agent Selection */}
                  <div className="mb-6">
                    <label className="text-xs font-bold text-gray-700 block mb-2">Select Agent to Expose</label>
                    <select value={selectedAgentId} onChange={e => setSelectedAgentId(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500">
                      <option value="">Choose an agent...</option>
                      {agents.map(a => (
                        <option key={a.id} value={a.id}>{a.icon} {a.name} — {a.description}</option>
                      ))}
                    </select>
                  </div>

                  {/* Generate Button */}
                  <button onClick={() => handleGenerate(plugin.id)} disabled={generating || !selectedAgentId || !selectedClientId}
                    className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold text-sm hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                    {generating ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><RefreshCw size={16} /> Generate Plugin Configuration</>}
                  </button>
                  {!selectedClientId && selectedAgentId && (
                    <p className="text-[10px] text-amber-600 text-center mt-2">⚠️ Select a client above — plugin queries will be billed to their token balance</p>
                  )}
                </div>

                {/* Generated Output */}
                {generatedConfig && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check size={18} />
                      <span className="text-sm font-bold">Plugin configuration generated!</span>
                    </div>

                    {generatedConfig.files && Object.entries(generatedConfig.files).map(([filename, content]: [string, any]) => (
                      <div key={filename} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                          <span className="text-xs font-mono font-semibold text-gray-700">{filename}</span>
                          <button onClick={() => copyToClipboard(content as string)} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                            <Copy size={12} /> Copy
                          </button>
                        </div>
                        <pre className="p-4 text-[11px] text-gray-800 overflow-x-auto bg-gray-900 text-green-300 font-mono leading-relaxed max-h-[400px] overflow-y-auto">
                          {content as string}
                        </pre>
                      </div>
                    ))}

                    {generatedConfig.instructions && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <h4 className="text-xs font-bold text-amber-800 mb-2">📋 Next Steps</h4>
                        <div className="text-xs text-amber-700 whitespace-pre-wrap">{generatedConfig.instructions}</div>
                      </div>
                    )}

                    {generatedConfig.endpoint && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="text-xs font-bold text-blue-800 mb-2">🔗 API Endpoint</h4>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs font-mono bg-white px-3 py-2 rounded-lg border border-blue-200 text-blue-800">{generatedConfig.endpoint}</code>
                          <button onClick={() => copyToClipboard(generatedConfig.endpoint)} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200">
                            <Copy size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
};

export default PluginsPage;
